import React, { useEffect, useRef, useState, BaseSyntheticEvent } from 'react';
import styled, { css } from 'styled-components';
//
import colors from 'theme/colors';

const { activeBackground, blueFormulaVariable, disabledBackground, disabledFont, fontBody, white } = colors;

const Container = styled.div`
  background-color: ${props => (props['data-active'] ? activeBackground : white)};
  border-radius: 3px;
  height: 96px;
  position: relative;
`;

const baseStyle = css`
  border-radius: 3px;
  font-family: 'system-ui';
  font-size: 12px;
  font-weight: 400;
  height: 76px;
  line-height: 1.5em;
  letter-spacing: 1px;
  margin: 0;
  padding: 10px;
  position: absolute;
  white-space: pre-wrap;
  width: calc(100% - 20px);
  word-wrap: break-word;
`;

const Backdrop = styled.div`
  ${baseStyle}
  z-index: 1;
  overflow: auto;
  pointer-events: none;
`;

const Highlights = styled.div`
  color: ${fontBody};

  mark {
    border-radius: 3px;
    color: ${blueFormulaVariable};
    background-color: transparent;
  }
`;

const Textarea = styled.textarea`
  ${baseStyle}
  background-color: transparent;
  border: none;
  color: transparent;
  caret-color: ${fontBody};
  resize: none;
  z-index: 2;

  :read-only {
    background-color: ${disabledBackground};
    color: ${disabledFont};
    cursor: not-allowed;
  }

  :focus {
    outline: none;
  }
`;

interface IProps {
  id?: string;
  formulaInputValue: string;
  isActive?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  readOnly?: boolean;
  setActiveFormula?: () => void;
  setCursorPosition?: (e: number) => void;
  setFormula?: (value: string) => void;
}

const timeOutDuration = 10;

function processBackSpace(
  cursorPosition: number,
  formulaInputValue: string,
  setFormula: ((value: string) => void) | undefined,
  setCursorPosition: ((e: number) => void) | undefined,
  setDidDeleteMarker: { (value: React.SetStateAction<boolean>): void; (arg0: boolean): void },
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>
) {
  // Check for adjacent marker bracket
  if (cursorPosition > 0 && formulaInputValue.charAt(cursorPosition - 1) === ']') {
    // Preserve characters after deletion point
    const postString = formulaInputValue.slice(cursorPosition);
    const subString = formulaInputValue.slice(0, cursorPosition);

    // Find position x of opening bracket
    const openingBracketPosition = subString.lastIndexOf('[');

    // If no corresponding bracket found, do nothing (cannot calculate where to end deletion)
    if (openingBracketPosition < 0) {
      return;
    }

    // Remove from 0 to x
    const preString = subString.slice(0, openingBracketPosition);

    // Return new string
    if (setFormula) {
      setFormula(preString + postString);
    }

    // Correct cursor positioning upon marker deletion
    if (setCursorPosition) {
      setDidDeleteMarker(true);
      setCursorPosition(openingBracketPosition);
    }
    setTimeout(() => {
      const { current } = textareaRef;
      if (current) {
        current.selectionStart = openingBracketPosition;
        current.selectionEnd = openingBracketPosition;
      }
    }, timeOutDuration);
  }
}

function processDelete(
  cursorPosition: number,
  formulaInputValue: string,
  setFormula: ((value: string) => void) | undefined,
  setCursorPosition: ((e: number) => void) | undefined,
  setDidDeleteMarker: { (value: React.SetStateAction<boolean>): void; (arg0: boolean): void },
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>
) {
  // Check for adjacent marker bracket
  if (cursorPosition < formulaInputValue.length && formulaInputValue.charAt(cursorPosition) === '[') {
    // Preserve characters before deletion point
    const preString = formulaInputValue.slice(0, cursorPosition);
    const subString = formulaInputValue.slice(cursorPosition);

    // Find position x of closing bracket
    const closingBracketPosition = subString.indexOf(']');

    // If no corresponding bracket found, do nothing (cannot calculate where to end deletion)
    if (closingBracketPosition < 0) {
      return;
    }

    // Remove from cursorPosition to x
    const postString = subString.slice(closingBracketPosition + 1);

    // Return new string
    if (setFormula) {
      setFormula(preString + postString);
    }

    // Correct cursor positioning upon marker deletion
    if (setCursorPosition) {
      setDidDeleteMarker(true);
      setCursorPosition(cursorPosition);
    }
    setTimeout(() => {
      const { current } = textareaRef;
      if (current) {
        current.selectionStart = cursorPosition;
        current.selectionEnd = cursorPosition;
      }
    }, timeOutDuration);
  }
}

export default ({
  id,
  setCursorPosition,
  setFormula,
  onChange,
  formulaInputValue,
  readOnly,
  isActive,
  setActiveFormula,
}: IProps) => {
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [highlightsHtml, setHighlightsHtml] = useState('');
  const [didDeleteMarker, setDidDeleteMarker] = useState(false);

  function handleClick(e: BaseSyntheticEvent) {
    if (!setCursorPosition) {
      return;
    }

    if (setActiveFormula) {
      setActiveFormula();
    }

    setCursorPosition(e.target.selectionStart);
  }

  function handleOnKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Detect if marker needs to be deleted
    const cursorPosition = (e.target as HTMLTextAreaElement)?.selectionStart;
    if (e.key === 'Delete') {
      processDelete(cursorPosition, formulaInputValue, setFormula, setCursorPosition, setDidDeleteMarker, textareaRef);
    } else if (e.key === 'Backspace') {
      processBackSpace(
        cursorPosition,
        formulaInputValue,
        setFormula,
        setCursorPosition,
        setDidDeleteMarker,
        textareaRef
      );
    }
  }

  function handleOnKeyUp(e: BaseSyntheticEvent) {
    if (!setCursorPosition) {
      return;
    }

    // Check for deletion of marker
    if (didDeleteMarker) {
      setDidDeleteMarker(false);
      return;
    }

    setCursorPosition(e.target.selectionStart);
  }

  // Sanitize formula input
  function sanitizeHtml(html: string): string {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  // Wrap all bracket-surrounded marker vars with <mark> tags for highlighting
  function applyHighlights(text: string) {
    return text.replace(/\[.*?\]/g, '<mark>$&</mark>');
  }

  function handleScroll() {
    if (backdropRef?.current && textareaRef && textareaRef.current) {
      const textareaScrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollTop = textareaScrollTop;
    }
  }

  useEffect(() => {
    if (textareaRef?.current) {
      const text = textareaRef.current.textContent;
      if (text) {
        const highlightedText = applyHighlights(sanitizeHtml(text));
        setHighlightsHtml(highlightedText);
      } else {
        setHighlightsHtml('');
      }
    }
  }, [formulaInputValue]);

  return (
    <Container data-active={isActive}>
      <Backdrop ref={backdropRef}>
        <Highlights dangerouslySetInnerHTML={{ __html: highlightsHtml }} />
      </Backdrop>
      <Textarea
        data-testid="tid-formula-textarea"
        id={id}
        onChange={onChange}
        onFocus={handleClick}
        onKeyDown={handleOnKeyDown}
        onKeyUp={handleOnKeyUp}
        onScroll={handleScroll}
        ref={textareaRef}
        value={formulaInputValue}
        spellCheck={false}
        readOnly={readOnly}
      />
    </Container>
  );
};
